import React from 'react';
import { Spinner } from 'react-bootstrap';

export type LoadingVariant = 'page' | 'section' | 'inline' | 'overlay';
export type LoadingSize = 'sm' | 'md' | 'lg';

interface LoadingSpinnerProps {
    /**
     * Display variant:
     * - 'page': Full viewport height, centered (for page-level loading)
     * - 'section': Centered with padding (for content sections)
     * - 'inline': Small inline spinner (for buttons, table cells)
     * - 'overlay': Absolute positioned overlay on parent container
     */
    variant?: LoadingVariant;

    /** Spinner size */
    size?: LoadingSize;

    /** Optional loading text (Swedish) */
    text?: string;

    /** Custom minimum height for section variant */
    minHeight?: string;

    /** Bootstrap color variant */
    color?: 'primary' | 'secondary' | 'dark' | 'light';
}

const sizeMap = {
    sm: { spinnerSize: 'sm' as const, fontSize: '0.875rem' },
    md: { spinnerSize: undefined, fontSize: '1rem' },
    lg: { spinnerSize: undefined, fontSize: '1.125rem' },
};

const variantStyles: Record<LoadingVariant, React.CSSProperties> = {
    page: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        gap: '12px',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 20px',
        gap: '12px',
    },
    inline: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: 10,
        gap: '12px',
    },
};

/**
 * Standardized loading spinner component.
 *
 * Usage:
 * ```tsx
 * // Full page loading
 * if (loading) return <LoadingSpinner variant="page" />;
 *
 * // Section loading with text
 * if (loading) return <LoadingSpinner variant="section" text="Laddar data..." />;
 *
 * // Inline in a button
 * <Button disabled={submitting}>
 *   {submitting && <LoadingSpinner variant="inline" size="sm" />}
 *   Spara
 * </Button>
 *
 * // Overlay on a container (parent needs position: relative)
 * <div style={{ position: 'relative' }}>
 *   {loading && <LoadingSpinner variant="overlay" />}
 *   <Content />
 * </div>
 * ```
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
    variant = 'section',
    size = 'md',
    text,
    minHeight,
    color = 'primary',
}) => {
    const { spinnerSize, fontSize } = sizeMap[size];
    const style: React.CSSProperties = {
        ...variantStyles[variant],
    };

    // Apply custom minHeight for section variant
    if (variant === 'section' && minHeight) {
        style.minHeight = minHeight;
    }

    // Explicit dimensions to ensure square spinner
    const spinnerStyle: React.CSSProperties = size === 'lg'
        ? { width: '3rem', height: '3rem' }
        : size === 'md'
        ? { width: '2rem', height: '2rem' }
        : { width: '1rem', height: '1rem' };

    return (
        <div style={style} role="status" aria-live="polite">
            <Spinner
                animation="border"
                variant={color}
                size={spinnerSize}
                style={spinnerStyle}
            >
                <span className="visually-hidden">Laddar...</span>
            </Spinner>
            {text && (
                <span
                    style={{
                        color: '#666',
                        fontSize,
                    }}
                >
                    {text}
                </span>
            )}
        </div>
    );
};

export default LoadingSpinner;

/**
 * Convenience component for full-page loading state.
 * Use as early return when loading page data.
 */
export const PageLoader: React.FC<{ text?: string }> = ({ text }) => (
    <LoadingSpinner variant="page" text={text} />
);

/**
 * Convenience component for section/card loading state.
 */
export const SectionLoader: React.FC<{ text?: string; minHeight?: string }> = ({
    text,
    minHeight
}) => (
    <LoadingSpinner variant="section" text={text} minHeight={minHeight} />
);

/**
 * Convenience component for inline loading (buttons, etc).
 */
export const InlineLoader: React.FC<{ text?: string }> = ({ text }) => (
    <LoadingSpinner variant="inline" size="sm" text={text} />
);
